import type {
  AdminListResponse,
  AdminEventSummary,
  CreateEventRequest,
  CreateGuestRequest,
  CreateGuestResponse,
  InviteResponse,
  Rsvp,
  SeatsResponse,
  SubmitRsvpRequest,
  UpdateGuestRequest,
  UpdateEventSettingsRequest,
} from "@firsttoast/shared";

// Same-origin: CloudFront routes /api/* to the HTTP API.
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export function getInvite(token: string): Promise<InviteResponse> {
  return req<InviteResponse>(`/api/invite/${encodeURIComponent(token)}`);
}

export function submitRsvp(
  token: string,
  body: SubmitRsvpRequest
): Promise<{ ok?: boolean; valid?: boolean; rsvp?: Rsvp }> {
  return req(`/api/invite/${encodeURIComponent(token)}/rsvp`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function adminLogin(password: string): Promise<{ token: string }> {
  return req(`/api/admin/login`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export function adminList(eventId: string, session: string): Promise<AdminListResponse> {
  return req<AdminListResponse>(
    `/api/admin/rsvps?eventId=${encodeURIComponent(eventId)}`,
    { headers: { authorization: `Bearer ${session}` } }
  );
}

export function adminCreateGuest(
  session: string,
  body: CreateGuestRequest
): Promise<CreateGuestResponse> {
  return req(`/api/admin/guests`, {
    method: "POST",
    headers: { authorization: `Bearer ${session}` },
    body: JSON.stringify(body),
  });
}

export function adminUpdateGuest(
  session: string,
  token: string,
  body: UpdateGuestRequest
): Promise<{ ok: boolean }> {
  return req(`/api/admin/guests/${encodeURIComponent(token)}`, {
    method: "PATCH",
    headers: { authorization: `Bearer ${session}` },
    body: JSON.stringify(body),
  });
}

export function adminDeleteGuest(session: string, token: string): Promise<{ ok: boolean }> {
  return req(`/api/admin/guests/${encodeURIComponent(token)}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${session}` },
  });
}

export function adminSendInvite(session: string, token: string): Promise<{ ok: boolean }> {
  return req(`/api/admin/guests/${encodeURIComponent(token)}/send`, {
    method: "POST",
    headers: { authorization: `Bearer ${session}` },
    body: JSON.stringify({ siteUrl: window.location.origin }),
  });
}

export function adminUpdateEvent(
  session: string,
  eventId: string,
  body: UpdateEventSettingsRequest
): Promise<{ ok: boolean }> {
  return req(`/api/admin/events/${encodeURIComponent(eventId)}`, {
    method: "PATCH",
    headers: { authorization: `Bearer ${session}` },
    body: JSON.stringify(body),
  });
}

export function adminEvents(session: string): Promise<{ events: AdminEventSummary[] }> {
  return req("/api/admin/events", { headers: { authorization: `Bearer ${session}` } });
}

export function adminCreateEvent(
  session: string,
  body: CreateEventRequest
): Promise<{ event: { eventId: string } }> {
  return req("/api/admin/events", {
    method: "POST",
    headers: { authorization: `Bearer ${session}` },
    body: JSON.stringify(body),
  });
}

export function getSeats(eventId: string): Promise<SeatsResponse> {
  return req<SeatsResponse>(`/api/seats/${encodeURIComponent(eventId)}`);
}
